import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '../common/Alert.js';
import { FormLayout } from '../common/index.js';
import loanReceivedService from '../../services/LoanReceivedService.js';
import { Plus, Filter, CheckCircle, ArrowLeft, Pencil, Trash2, MoreVertical, HandCoins, XCircle, CreditCard, Eye, EyeOff, X } from "lucide-react";

const AddLoanReceived = () => {
  const [formData, setFormData] = useState({
    lender_type: '',
    lender_name: '',
    lender_contact: '',
    lender_email: '',
    lender_address: '',
    account_number: '',
    ifsc_code: '',
    loan_type: '',
    loan_amount: '',
    interest_rate: '0',
    processing_fee: '0',
    loan_date: new Date().toISOString().split('T')[0],
    due_date: '',
    disbursed_date: '',
    repayment_frequency: '',
    total_installments: '1',
    installment_amount: '0',
    loan_account_number: '',
    collateral_details: '',
    purpose: '',
    remarks: '',
    auto_approve: false
  });
  
  const [loanAgreement, setLoanAgreement] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [loanTypes, setLoanTypes] = useState([]);
  const [lenderTypes, setLenderTypes] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    calculateTotalAmount();
  }, [formData.loan_amount, formData.interest_rate, formData.processing_fee, formData.loan_date, formData.due_date]);

  const fetchData = async () => {
    try {
      const [typesRes, lenderTypesRes, freqRes] = await Promise.all([
        loanReceivedService.getLoanTypes(),
        loanReceivedService.getLenderTypes(),
        loanReceivedService.getFrequencies()
      ]);
      
      setLoanTypes(typesRes.data || []);
      setLenderTypes(lenderTypesRes.data || []);
      setFrequencies(freqRes.data || []);
    } catch (err) {
      Alert.error('Failed to load data');
      console.error(err);
    }
  };

  const calculateTotalAmount = () => {
    const principal = parseFloat(formData.loan_amount) || 0;
    const rate = parseFloat(formData.interest_rate) || 0;
    const fee = parseFloat(formData.processing_fee) || 0;
    
    if (principal > 0 && rate > 0 && formData.loan_date && formData.due_date) {
      const startDate = new Date(formData.loan_date);
      const endDate = new Date(formData.due_date);
      const days = Math.max(0, (endDate - startDate) / (1000 * 60 * 60 * 24));
      const interest = (principal * rate * days) / (365 * 100);
      setTotalAmount(principal + interest + fee);
    } else {
      setTotalAmount(principal + fee);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDocumentChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('File must be less than 5MB');
        return;
      }
      setLoanAgreement(file);
      
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => setDocumentPreview(reader.result);
        reader.readAsDataURL(file);
      } else {
        setDocumentPreview('pdf');
      }
    }
  };

  const removeDocument = () => {
    setLoanAgreement(null);
    setDocumentPreview(null);
    const fileInput = document.getElementById('agreement-input');
    if (fileInput) fileInput.value = '';
  };

  const validateForm = () => {
    if (!formData.lender_type) {
      Alert.error('Please select lender type');
      return false;
    }
    if (!formData.lender_name) {
      Alert.error('Please enter lender name');
      return false;
    }
    if (!formData.lender_contact) {
      Alert.error('Please enter lender contact');
      return false;
    }
    if (!formData.loan_type) {
      Alert.error('Please select loan type');
      return false;
    }
    if (!formData.loan_amount || parseFloat(formData.loan_amount) <= 0) {
      Alert.error('Please enter valid loan amount');
      return false;
    }
    if (!formData.loan_date) {
      Alert.error('Please select loan date');
      return false;
    }
    if (!formData.due_date) {
      Alert.error('Please select due date');
      return false;
    }
    if (new Date(formData.due_date) <= new Date(formData.loan_date)) {
      Alert.error('Due date must be after loan date');
      return false;
    }
    if (!formData.repayment_frequency) {
      Alert.error('Please select repayment frequency');
      return false;
    }
    if (!formData.purpose) {
      Alert.error('Please enter loan purpose');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);

    try {
      await loanReceivedService.create(formData, loanAgreement);
      Alert.success('Loan record created successfully!');
      setTimeout(() => navigate('/loans-received'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create loan');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  return (
    <FormLayout
      title="Add Loan Received"
      icon={CreditCard}
      onSubmit={handleSubmit}
      submitLabel={loading ? 'Creating...' : 'Create Loan'}
      loading={loading}
      onCancel={() => navigate('/loans-received')}
      maxWidth="max-w-7xl"
    >

                  {/* Section 1: Lender Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Lender Information
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Lender Type <span class="text-red-500">*</span>
                        </label>
                        <select
                          name="lender_type"
                          value={formData.lender_type}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          required
                        >
                          <option value="">Select Type</option>
                          {lenderTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Lender Name <span class="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="lender_name"
                          value={formData.lender_name}
                          onChange={handleChange}
                          placeholder="Enter lender name"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Contact Number <span class="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          name="lender_contact"
                          value={formData.lender_contact}
                          onChange={handleChange}
                          placeholder="Enter contact number"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Email
                        </label>
                        <input
                          type="email"
                          name="lender_email"
                          value={formData.lender_email}
                          onChange={handleChange}
                          placeholder="Enter email address"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Account Number
                        </label>
                        <input
                          type="text"
                          name="account_number"
                          value={formData.account_number}
                          onChange={handleChange}
                          placeholder="Enter account number"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          IFSC Code
                        </label>
                        <input
                          type="text"
                          name="ifsc_code"
                          value={formData.ifsc_code}
                          onChange={handleChange}
                          placeholder="Enter IFSC code"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div className="sm:col-span-2">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Address
                        </label>
                        <textarea
                          name="lender_address"
                          value={formData.lender_address}
                          onChange={handleChange}
                          placeholder="Enter full address"
                          rows="2"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Loan Details */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Loan Details
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Loan Type <span class="text-red-500">*</span>
                        </label>
                        <select
                          name="loan_type"
                          value={formData.loan_type}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          required
                        >
                          <option value="">Select Type</option>
                          {loanTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Loan Amount <span class="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="loan_amount"
                          value={formData.loan_amount}
                          onChange={handleChange}
                          placeholder="Enter loan amount"
                          step="0.01"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Interest Rate (% per annum)
                        </label>
                        <input
                          type="number"
                          name="interest_rate"
                          value={formData.interest_rate}
                          onChange={handleChange}
                          placeholder="Enter interest rate"
                          step="0.01"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Processing Fee
                        </label>
                        <input
                          type="number"
                          name="processing_fee"
                          value={formData.processing_fee}
                          onChange={handleChange}
                          placeholder="Enter processing fee"
                          step="0.01"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Total Amount (with Interest & Fee)
                        </label>
                        <div className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm bg-gray-50 text-sm sm:text-base font-semibold text-green-600">
                          {formatCurrency(totalAmount)}
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Loan Account Number
                        </label>
                        <input
                          type="text"
                          name="loan_account_number"
                          value={formData.loan_account_number}
                          onChange={handleChange}
                          placeholder="Enter loan account number"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Loan Date <span class="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          name="loan_date"
                          value={formData.loan_date}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Due Date <span class="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          name="due_date"
                          value={formData.due_date}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Disbursed Date
                        </label>
                        <input
                          type="date"
                          name="disbursed_date"
                          value={formData.disbursed_date}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Repayment Details */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Repayment Details
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Repayment Frequency <span class="text-red-500">*</span>
                        </label>
                        <select
                          name="repayment_frequency"
                          value={formData.repayment_frequency}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          required
                        >
                          <option value="">Select Frequency</option>
                          {frequencies.map((freq) => (
                            <option key={freq.value} value={freq.value}>
                              {freq.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Total Installments
                        </label>
                        <input
                          type="number"
                          name="total_installments"
                          value={formData.total_installments}
                          onChange={handleChange}
                          placeholder="Enter total installments"
                          min="1"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Installment Amount
                        </label>
                        <input
                          type="number"
                          name="installment_amount"
                          value={formData.installment_amount}
                          onChange={handleChange}
                          placeholder="Enter installment amount"
                          step="0.01"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Additional Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Additional Information
                    </h3>
                    <div className="space-y-4 sm:space-y-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Loan Purpose <span class="text-red-500">*</span>
                        </label>
                        <textarea
                          name="purpose"
                          value={formData.purpose}
                          onChange={handleChange}
                          placeholder="Enter the purpose of the loan"
                          rows="3"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Collateral Details
                        </label>
                        <textarea
                          name="collateral_details"
                          value={formData.collateral_details}
                          onChange={handleChange}
                          placeholder="Enter collateral details (if any)"
                          rows="3"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Loan Agreement Document
                        </label>
                        <input
                          id="agreement-input"
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={handleDocumentChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                        <p className="text-xs text-gray-600 mt-1">Upload loan agreement document (Max 5MB)</p>
                        {documentPreview && (
                          <div className="mt-3">
                            {documentPreview === 'pdf' ? (
                              <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-sm">
                                <span className="text-2xl">📄</span>
                                <span className="text-sm text-gray-700">{loanAgreement.name}</span>
                                <button
                                  type="button"
                                  onClick={removeDocument}
                                  className="ml-auto text-red-500 hover:text-red-600"
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <div className="relative inline-block">
                                <img 
                                  src={documentPreview} 
                                  alt="Document Preview" 
                                  className="w-32 h-32 sm:w-40 sm:h-40 object-cover border-2 border-gray-300 rounded-md"
                                />
                                <button
                                  type="button"
                                  onClick={removeDocument}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                                >
                                  ✕
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Remarks
                        </label>
                        <textarea
                          name="remarks"
                          value={formData.remarks}
                          onChange={handleChange}
                          placeholder="Enter any additional remarks"
                          rows="3"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base"
                        />
                      </div>

                      <div>
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            name="auto_approve"
                            checked={formData.auto_approve}
                            onChange={handleChange}
                            className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-brand"
                          />
                          <span className="text-gray-700">Auto-approve this loan</span>
                        </label>
                      </div>
                    </div>
                  </div>

    </FormLayout>
  );
};

export default AddLoanReceived;